/** Widget API client (AGENTS.md 9.1). Answers are queued and retried; the UI never waits for the network. */
import type {
  BookingOptionsResponse,
  BookingStatusResponse,
  BookingSummary,
  HoldBookingResponse,
  CreateBookingRequest,
  SlotsResponse,
  CreateSessionRequest,
  CreateSessionResponse,
  CreateUploadRequest,
  CreateUploadResponse,
  HandoffLinkRequest,
  HandoffLinkResponse,
  HandoffRequest,
  HandoffResponse,
  ResumeResponse,
  UpsertAnswerRequest,
  UpsertAnswerResponse,
  WidgetConfigResponse,
  WidgetEvent,
} from "@wapadesk/schema";
import { withEmbed } from "./embed";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(code);
  }
}

export class ApiClient {
  token: string | undefined;
  constructor(
    private readonly origin: string,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  private async call<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (auth && this.token) headers["authorization"] = `Bearer ${this.token}`;
    const payload = body === undefined ? undefined : JSON.stringify(body);
    const res = await this.fetchImpl(`${this.origin}${withEmbed(path)}`, {
      method,
      headers,
      body: payload,
      credentials: "omit",
      mode: "cors",
      // Small writes survive the page being closed or reloaded mid-save (5.1 item 5, D2).
      ...(method !== "GET" && (payload?.length ?? 0) < 60_000 ? { keepalive: true } : {}),
    });
    if (res.status === 204) return undefined as T;
    const json = (await res.json().catch(() => ({}))) as { error?: string; details?: unknown };
    if (!res.ok) throw new ApiClientError(res.status, json.error ?? "http_error", json.details);
    return json as T;
  }

  config(clinicId: string, pageUrl: string): Promise<WidgetConfigResponse> {
    return this.call(
      "GET",
      `/v1/widget/config?clinic=${encodeURIComponent(clinicId)}&page=${encodeURIComponent(pageUrl)}`,
      undefined,
      false,
    );
  }
  configBySlug(slug: string, pageUrl: string): Promise<WidgetConfigResponse> {
    return this.call(
      "GET",
      `/v1/widget/config?slug=${encodeURIComponent(slug)}&page=${encodeURIComponent(pageUrl)}`,
      undefined,
      false,
    );
  }
  createSession(body: CreateSessionRequest): Promise<CreateSessionResponse> {
    return this.call("POST", "/v1/widget/sessions", body, false);
  }
  resume(): Promise<ResumeResponse> {
    return this.call("GET", "/v1/widget/leads/current");
  }
  upsertAnswer(body: UpsertAnswerRequest): Promise<UpsertAnswerResponse> {
    return this.call("PATCH", "/v1/widget/leads/current/answers", body);
  }
  createUpload(body: CreateUploadRequest): Promise<CreateUploadResponse> {
    return this.call("POST", "/v1/widget/leads/current/uploads", body);
  }
  handoff(body: HandoffRequest): Promise<HandoffResponse> {
    return this.call("POST", "/v1/widget/leads/current/handoff", body);
  }
  handoffLink(body: HandoffLinkRequest): Promise<HandoffLinkResponse> {
    return this.call("POST", "/v1/widget/leads/current/handoff-link", body);
  }
  bookingOptions(): Promise<BookingOptionsResponse> {
    return this.call("GET", "/v1/widget/booking/options");
  }
  slots(q: {
    type: string;
    doctor?: string;
    location?: string;
    from: string;
    days: number;
  }): Promise<SlotsResponse> {
    const qs = new URLSearchParams({ type: q.type, from: q.from, days: String(q.days) });
    if (q.doctor) qs.set("doctor", q.doctor);
    if (q.location) qs.set("location", q.location);
    return this.call("GET", `/v1/widget/booking/slots?${qs.toString()}`);
  }
  book(body: CreateBookingRequest): Promise<BookingSummary> {
    return this.call("POST", "/v1/widget/leads/current/booking", body);
  }
  hold(body: CreateBookingRequest): Promise<HoldBookingResponse> {
    return this.call("POST", "/v1/widget/leads/current/booking/hold", body);
  }
  bookingStatus(appointmentId: string): Promise<BookingStatusResponse> {
    return this.call(
      "GET",
      `/v1/widget/booking/status?appointment=${encodeURIComponent(appointmentId)}`,
    );
  }
  events(events: WidgetEvent[]): Promise<void> {
    return this.call("POST", "/v1/widget/events", { events });
  }
}

/** Serial queue with retry for answer saves (optimistic UI, section 17). */
export class AnswerQueue {
  private chain: Promise<void> = Promise.resolve();
  pending = 0;
  constructor(
    private readonly api: ApiClient,
    private readonly onResult: (nodeId: string, r: UpsertAnswerResponse | ApiClientError) => void,
    private readonly delays = [500, 1500, 4000],
  ) {}

  enqueue(body: UpsertAnswerRequest): void {
    this.pending++;
    this.chain = this.chain.then(async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const r = await this.api.upsertAnswer(body);
          this.onResult(body.nodeId, r);
          break;
        } catch (err) {
          const e = err instanceof ApiClientError ? err : new ApiClientError(0, "network");
          if (e.status >= 400 && e.status < 500) {
            this.onResult(body.nodeId, e);
            break;
          }
          const d = this.delays[Math.min(attempt, this.delays.length - 1)]!;
          await new Promise((r) => setTimeout(r, d));
        }
      }
      this.pending--;
    });
  }
}
